import {
    IApiAdvertisement,
    IApiBackground,
    IApiBidirectionalTranslation,
    IApiFestivalSetting,
    IApiGreetingsWithToggle,
    IApiHintLanguage,
    IApiHintsObject,
    IApiInteraction,
    IApiInteractionData,
    IApiLayoutMode,
    IApiPhotoSettings,
    IApiQRCode,
    IApiSubtitleSpeed,
    IApiTimeAlerts,
    IApiVolume,
    IApiWeather,
    IKioskSettingNative,
    IScheduleMarqueeNew,
    ITimeAlertGroup,
} from "../../types";

/**
 * 預設時間提醒設定 (預設關閉)
 */
export const DEFAULT_TIME_ALERTS: IApiTimeAlerts = {
    enabled_time_alerts: false,
    data: [],
};

export const DEFAULT_TIME_GROUP: ITimeAlertGroup = {
    id: "",
    time: "09:00",
    repeat_days: [1, 2, 3, 4, 5],
    type: "voice",
    voice: {
        country: "",
        station_code: "",
        station_name: "",
    },
};
/**
 * 預設語言設定
 */
export const DEFAULT_LANGUAGE_SETTINGS = {
    defaultLanguage: "zh-TW",
    languageOptions: ["zh-TW", "en-US", "ja-JP", "ko-KR"],
    languageDisplayMode: "dropdown" as const,
};
/**
 * 預設招呼詞
 */
export const DEFAULT_GREETINGS: IApiGreetingsWithToggle = {
    enabled: false,
    data: [],
};
/**
 * 預設提示詞資料 (語言選項)
 * @language 語言
 * @options 提示詞(共三個)
 */
export const DEFAULT_HINTS_DATA: IApiHintLanguage[] = [
    {
        language: "zh-TW",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做些什麼?"],
    },
    {
        language: "taiwanese",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做些什麼?"],
    },
    {
        language: "zh-CN",
        options: ["你好", "可以帮我介绍一下吗?", "你可以做些什么?"],
    },
    {
        language: "zh-HK",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做啲乜?"],
    },
    {
        language: "en-US",
        options: ["Hello", "Can you introduce it to me?", "What can you do?"],
    },
    {
        language: "en-GB",
        options: ["Hello", "Can you introduce it to me?", "What can you do?"],
    },
    {
        language: "ja-JP",
        options: ["こんにちは", "紹介してもらえますか?", "何ができるでしょうか?"],
    },
    {
        language: "ko-KR",
        options: ["안녕하세요", "소개해 주시겠습니까?", "무엇을 할 수 있습니까?"],
    },
    {
        language: "fr-FR",
        options: ["Bonjour", "Pouvez-vous me le présenter?", "Que pouvez-vous faire?"],
    },
    {
        language: "es-ES",
        options: ["Hola", "¿Puedes presentármelo?", "¿Qué puedes hacer?"],
    },
    {
        language: "ca-ES",
        options: ["Hola", "Me la pots presentar?", "Què pots fer?"],
    },
    {
        language: "vi-VN",
        options: ["Xin chào", "Bạn có thể giới thiệu nó cho tôi không?", "Bạn có thể làm gì?"],
    },
    {
        language: "th-TH",
        options: ["สวัสดี", "คุณช่วยแนะนำให้ฉันรู้จักได้ไหม?", "คุณทำอะไรได้บ้าง?"],
    },
    {
        language: "km-KH",
        options: ["សួស្តី", "តើអ្នកអាចណែនាំខ្ញុំបានទេ?", "តើអ្នកអាចធ្វើអ្វីបានខ្លះ?"],
    },
    {
        language: "ro-RO",
        options: ["Buna ziua", "Ai putea să mi-l prezinți?", "Ce poți face?"],
    },
    {
        language: "ru-RU",
        options: ["Привет", "Не могли бы вы мне его представить?", "Что вы можете сделать?"],
    },
];

/**
 * 預設提示詞 (新格式：物件形式)
 * @hints_icon 圖示類型 (finger | speaker)
 * @data 語言資料陣列
 */
export const DEFAULT_HINTS: IApiHintsObject = {
    hints_icon: "finger",
    data: DEFAULT_HINTS_DATA,
};
/**
 * 預設背景設定
 */
export const DEFAULT_BACKGROUND_SETTINGS: IApiBackground = {
    v: {
        url: "",
        updated_time: "",
        position_x: 0,
        position_y: 0,
    },
    h: {
        url: "",
        updated_time: "",
        position_x: 0,
        position_y: 0,
    },
};
/**
 * 預設拍照設定
 */
export const DEFAULT_PHOTO_SETTINGS: IApiPhotoSettings = {
    enabled_photo: false,
    filter: {
        customized: false,
        filters_url: [],
        enable_default: true,
    },
    character: {
        enable: false,
    },
    line_id: "@159kdmqh",
    take_photo_sec: 3,
    close_result_sec: 10,
};
/**
 * 預設 QR Code 設定
 */
export const DEFAULT_QRCODE_SETTINGS: IApiQRCode = {
    QRcode_show_mode: false,
    QRcode_Url: "",
    inputMode: "url",
};
/**
 * 預設音量設定
 */
export const DEFAULT_VOLUME_SETTINGS: IApiVolume = {
    voice: 1,
    effect: 0.6,
    bgm_max: 0.8,
    bgm_min: 0.5,
    bgm_url: "",
    ad_video_max: 0.8,
    ad_video_min: 0.5,
};
/**
 * 預設節慶設定
 */
export const DEFAULT_FESTIVAL_SETTINGS: IApiFestivalSetting = {
    use_festival: false,
    setting: {
        bgm: false,
        bg: false,
        bg_obj: false,
        effect: false,
        character: false,
        character_is_conservative: false,
        filter: false,
        filter_keep_default: false,
    },
};
/**
 * 預設廣告設定
 */
export const DEFAULT_AD_SETTINGS: IApiAdvertisement = {
    enabled: false,
    shuffle_mode: false,
    media: [],
};
/**
 * 預設跑馬燈設定
 */
export const DEFAULT_MARQUEE_SETTINGS: IScheduleMarqueeNew = {
    enable_marquee: false,
    position: "top",
    data: [],
};
/**
 * 預設天氣設定
 */
export const DEFAULT_WEATHER_SETTINGS: IApiWeather = {
    enabled_weather: false,
    display_description: false,
    station_name: "",
    station_code: "",
    country: "",
};

/**
 * 預設互動設定 (觸控 touch、辨識 gesture、自訂 custom)
 * 觸控與辨識模式的預設提示文字
 */
export const DEFAULT_TOUCH_HINTS: IApiInteractionData[] = [
    { language: "zh-TW", content: "觸控這裡開始互動" },
    { language: "en-US", content: "Touch here to start" },
    { language: "ja-JP", content: "タッチしてスタート" },
    { language: "ko-KR", content: "터치하여 시작" },
    { language: "km-KH", content: "ចុចទីនេះដើម្បីចាប់ផ្តើម" },
];

export const DEFAULT_GESTURE_HINTS: IApiInteractionData[] = [
    { language: "zh-TW", content: "揮手開始互動" },
    { language: "en-US", content: "Wave to start" },
    { language: "ja-JP", content: "手を振って開始" },
    { language: "ko-KR", content: "손을 흔들어 시작" },
    { language: "km-KH", content: "គ្រវីដៃដើម្បីចាប់ផ្តើម" },
];

export const DEFAULT_INTERACTION_SETTINGS: IApiInteraction = {
    enabled_interaction: false,
    interaction_mode: "touch",
    data: [],
};

/**
 * 預設切版模式設定
 */
export const DEFAULT_LAYOUT_SETTINGS: IApiLayoutMode = {
    mode: "standard",
    ad_position: "right",
};

/**
 * 預設雙向翻譯設定
 */
export const DEFAULT_BIDIRECTIONAL_TRANSLATION_SETTINGS: IApiBidirectionalTranslation = {
    enabled: false,
};

/**
 * 預設字幕滾動速度設定
 */
export const DEFAULT_SUBTITLE_SPEED: IApiSubtitleSpeed[] = [{ language: "zh-TW", speed: 1.0 }];
/**
 * 完整預設 Kiosk Native 設定
 * 用於新角色建立時的初始設定
 */
export const DEFAULT_KIOSK_NATIVE_SETTINGS: IKioskSettingNative = {
    general: DEFAULT_LANGUAGE_SETTINGS,
    schedule_marquee: DEFAULT_MARQUEE_SETTINGS,
    ad: DEFAULT_AD_SETTINGS,
    greetings: DEFAULT_GREETINGS,
    hints: DEFAULT_HINTS,
    QRcode: DEFAULT_QRCODE_SETTINGS,
    festival_setting: DEFAULT_FESTIVAL_SETTINGS,
    photo: DEFAULT_PHOTO_SETTINGS,
    background: DEFAULT_BACKGROUND_SETTINGS,
    volume: DEFAULT_VOLUME_SETTINGS,
    weather: DEFAULT_WEATHER_SETTINGS,
    time_alerts: DEFAULT_TIME_ALERTS,
    interaction: DEFAULT_INTERACTION_SETTINGS,
    layout_mode: DEFAULT_LAYOUT_SETTINGS,
    subtitle_speed: DEFAULT_SUBTITLE_SPEED,
    bidirectional_translation: DEFAULT_BIDIRECTIONAL_TRANSLATION_SETTINGS,
};
